import type {
  ClassDef,
  Drops,
  Economy,
  ItemDef,
  MobDef,
  NpcDef,
  QuestDef,
  SkillDef,
  StructureDef,
  Wheel,
  WheelOdds,
  Zone,
} from "./schema";

/** Cross-file checks Zod cannot express: unique ids and references between files. */
export function validateReferences(d: {
  zones: Zone[];
  classes: ClassDef[];
  skills: SkillDef[];
  mobs: MobDef[];
  structures?: StructureDef[];
  items?: ItemDef[];
  drops?: Drops;
  npcs?: NpcDef[];
  quests?: QuestDef[];
  wheel?: Wheel;
  wheelOdds?: WheelOdds;
  economy?: Economy;
}): void {
  const unique = (ids: string[], kind: string) => {
    const seen = new Set<string>();
    for (const id of ids) {
      if (seen.has(id)) throw new Error(`Duplicate ${kind} id: ${id}`);
      seen.add(id);
    }
  };
  unique(
    d.zones.map((z) => z.id),
    "zone",
  );
  unique(
    d.classes.map((c) => c.id),
    "class",
  );
  unique(
    d.skills.map((s) => s.id),
    "skill",
  );
  unique(
    d.mobs.map((m) => m.id),
    "mob",
  );

  const mobIds = new Set(d.mobs.map((m) => m.id));
  if (d.items) {
    unique(
      d.items.map((i) => i.id),
      "item",
    );
    for (const i of d.items) {
      if ((i.kind === "consumable" || i.kind === "material") && i.slot !== "none") {
        throw new Error(`Item ${i.id}: consumables and materials cannot be equipped`);
      }
      if (i.kind === "consumable" && !i.use && !i.opens)
        throw new Error(`Item ${i.id}: consumable without use`);
    }
  }
  if (d.items) {
    const itemIds = new Set(d.items.map((i) => i.id));
    for (const c of d.classes) {
      for (const s of c.starterItems) {
        if (!itemIds.has(s.item)) throw new Error(`Class ${c.id}: unknown starter item ${s.item}`);
      }
    }
    const r = d.economy?.referral;
    for (const g of [
      ...(d.economy?.newAccountGift ?? []),
      ...(r?.referrer ?? []),
      ...(r?.referred ?? []),
    ]) {
      const item = d.items.find((i) => i.id === g.item);
      if (!item) throw new Error(`Gift or invite reward: unknown item ${g.item}`);
      if (g.qty > (item.stack ?? 1))
        throw new Error(`Gift or invite reward: ${g.item} over its stack`);
    }
  }
  if (d.npcs && d.items) {
    unique(
      d.npcs.map((n) => n.id),
      "npc",
    );
    const itemIds = new Set(d.items.map((i) => i.id));
    const zoneIds = new Set(d.zones.map((z) => z.id));
    for (const n of d.npcs) {
      if (!zoneIds.has(n.zone)) throw new Error(`NPC ${n.id}: unknown zone ${n.zone}`);
      for (const s of n.shop)
        if (!itemIds.has(s.item)) throw new Error(`NPC ${n.id}: unknown item ${s.item}`);
      if (n.shop.length > 0 && !n.services.includes("buy"))
        throw new Error(`NPC ${n.id}: shop without buy service`);
    }
  }
  if (d.drops && d.items) {
    const itemIds = new Set(d.items.map((i) => i.id));
    for (const [mob, table] of Object.entries(d.drops.mobs)) {
      if (!mobIds.has(mob)) throw new Error(`Drops for unknown mob ${mob}`);
      for (const roll of table.rolls) {
        for (const [id] of roll.pool)
          if (!itemIds.has(id)) throw new Error(`Drops for ${mob}: unknown item ${id}`);
      }
    }
  }
  if (d.structures) {
    unique(
      d.structures.map((s) => s.id),
      "structure",
    );
    const structureIds = new Set(d.structures.map((s) => s.id));
    for (const zone of d.zones) {
      for (const s of zone.town?.structures ?? []) {
        if (!structureIds.has(s.type))
          throw new Error(`Zone ${zone.id} places unknown structure ${s.type}`);
      }
    }
  }
  for (const zone of d.zones) {
    for (const spawn of zone.mobSpawns) {
      if (!mobIds.has(spawn.mob))
        throw new Error(`Zone ${zone.id} spawns unknown mob ${spawn.mob}`);
      if (spawn.times && !zone.schedule)
        throw new Error(`Zone ${zone.id}: scheduled ${spawn.mob} needs a schedule timezone`);
    }
    if (zone.bowl && zone.bowl.radius <= zone.bowl.floorRadius)
      throw new Error(`Zone ${zone.id}: bowl radius must be larger than its floor`);
  }
  const zoneById = new Map(d.zones.map((z) => [z.id, z]));
  for (const zone of d.zones) {
    for (const g of zone.portals ?? []) {
      if (g.only && !zoneById.has(g.only))
        throw new Error(`Zone ${zone.id}: gate to unknown zone ${g.only}`);
    }
    if (zone.war) {
      if (!zone.bases) throw new Error(`Zone ${zone.id}: a war zone needs bases`);
      for (const c of zone.war.closes)
        if (!zoneById.has(c)) throw new Error(`Zone ${zone.id}: war closes unknown zone ${c}`);
      if (!zoneById.has(zone.war.returnTo))
        throw new Error(`Zone ${zone.id}: war returns to unknown zone ${zone.war.returnTo}`);
      for (const race of ["human", "orc"] as const) {
        const k = zone.war.keepers[race];
        const mob = d.mobs.find((m) => m.id === k.mob);
        if (!mob) throw new Error(`Zone ${zone.id}: unknown ${race} keeper ${k.mob}`);
        if (mob.nation !== race)
          throw new Error(`Zone ${zone.id}: ${race} keeper ${k.mob} must have nation ${race}`);
      }
    }
    if (zone.clash) {
      const c = zone.clash;
      if (!zone.bases) throw new Error(`Zone ${zone.id}: the Crystal Clash arena needs bases`);
      if (!zoneById.has(c.returnTo))
        throw new Error(`Zone ${zone.id}: the Crystal Clash returns to unknown zone ${c.returnTo}`);
      const itemIds = new Set(d.items?.map((i) => i.id) ?? []);
      let floor = 0;
      for (const b of c.brackets) {
        if (b.minLevel > b.maxLevel || b.minLevel <= floor)
          throw new Error(`Zone ${zone.id}: Crystal Clash brackets must climb without overlap`);
        floor = b.maxLevel;
        if (!d.mobs.find((m) => m.id === b.crystal)?.crystal)
          throw new Error(`Zone ${zone.id}: ${b.crystal} is not a crystal`);
        for (const id of [b.win, b.lose])
          if (d.items && !itemIds.has(id)) throw new Error(`Zone ${zone.id}: unknown coffer ${id}`);
      }
    }
    if (zone.timed) {
      const from = zoneById.get(zone.timed.from);
      if (!from) throw new Error(`Zone ${zone.id}: opens from unknown zone ${zone.timed.from}`);
      if (!from.portals?.some((g) => g.only === zone.id))
        throw new Error(`Zone ${zone.id}: ${from.id} has no gate to it`);
    }
  }
  if (d.wheel && d.items) {
    const itemIds = new Set(d.items.map((i) => i.id));
    for (const [tier, t] of Object.entries(d.wheel.tiers)) {
      if (!itemIds.has(t.gem)) throw new Error(`Wheel ${tier}: unknown gem ${t.gem}`);
      for (const seg of t.segments) {
        const kinds = [seg.empty, seg.gold, seg.item, seg.pool].filter((x) => x !== undefined);
        if (kinds.length !== 1)
          throw new Error(`Wheel ${tier}: a prize is empty, gold, an item or a pool`);
        for (const id of [seg.item, ...(seg.pool ?? [])])
          if (id && !itemIds.has(id)) throw new Error(`Wheel ${tier}: unknown item ${id}`);
      }
      const odds = d.wheelOdds?.[tier as keyof WheelOdds];
      if (d.wheelOdds && odds?.length !== t.segments.length)
        throw new Error(`Wheel ${tier}: one weight per segment`);
    }
  }
  if (d.quests) {
    unique(
      d.quests.map((q) => q.id),
      "quest",
    );
    const zoneMobs = new Map(d.zones.map((z) => [z.id, new Set(z.mobSpawns.map((s) => s.mob))]));
    for (const q of d.quests) {
      for (const z of q.zones) {
        const spawns = zoneMobs.get(z);
        if (!spawns) throw new Error(`Quest ${q.id}: unknown zone ${z}`);
        if (q.goal.kind !== "pvp" && !spawns.has(q.goal.mob))
          throw new Error(`Quest ${q.id}: ${q.goal.mob} does not live in ${z}`);
        if (q.goal.kind === "pvp" && !d.zones.find((x) => x.id === z)?.pvp)
          throw new Error(`Quest ${q.id}: no player fights in ${z}`);
      }
    }
  }
  for (const s of d.skills) {
    const hasDamage = s.effects.some((e) => e.type === "damage");
    if (s.effects.some((e) => e.type === "status") && !hasDamage && s.target !== "enemy") {
      throw new Error(`Skill ${s.id}: status effects need a damage effect or an enemy target`);
    }
    if ((s.target === "enemy" || s.target === "ally" || s.target === "point") && s.range <= 0) {
      throw new Error(`Skill ${s.id}: targeted skills need range > 0`);
    }
  }
}
